// packages/frontend/src/app/providers/SessionProvider.tsx
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback,
} from 'react';
import { getMe } from '../../services/apiMe';

/** Local app shape used throughout the UI */
type Me =
  | {
      userId: string;
      email?: string;
      hasMembership?: boolean;
      ownedProducts?: string[];
    }
  | null;

/** Minimal backend response shape (kept local to avoid coupling) */
type MeResponse = {
  authenticated?: boolean;
  userId?: string;
  user?: { id?: string; email?: string };
  telegramId?: string;
  email?: string;
  hasMembership?: boolean;
  hasAccess?: boolean;
  ownedProducts?: string[];
  [k: string]: unknown;
};

type SessionCtx = {
  me: Me;
  isAuthenticated: boolean;
  loading: boolean;
  error?: string;
  refresh: () => Promise<void>;
  logout: () => void;
};

const SessionContext = createContext<SessionCtx | undefined>(undefined);

/** Map arbitrary backend payload to our local Me shape (or null) */
function toMe(data: unknown): Me {
  const d = data as MeResponse | null | undefined;
  if (!d || d.authenticated === false) return null;

  const userId =
    d.userId ?? d.user?.id ?? d.telegramId ?? null;
  if (!userId) return null;

  const owned =
    Array.isArray(d.ownedProducts)
      ? d.ownedProducts.map(String)
      : [];

  return {
    userId: String(userId),
    email: d.email ?? d.user?.email ?? undefined,
    hasMembership: Boolean(d.hasMembership ?? d.hasAccess ?? false),
    ownedProducts: owned,
  };
}

export default function SessionProvider({ children }: { children: React.ReactNode }) {
  const [me, setMe] = useState<Me>(null);
  const [loading, setLoading] = useState(true);
  const [error, setErr] = useState<string | undefined>(undefined);

  const refresh = useCallback(async () => {
    try {
      setLoading(true);
      setErr(undefined);
      const data = (await getMe()) as unknown; // backend JSON
      setMe(toMe(data));
    } catch (e: unknown) {
      setMe(null);
      setErr((e as any)?.message || 'Failed to load session');
    } finally {
      setLoading(false);
    }
  }, []);

  const logout = useCallback(() => {
    // (Optional) call your backend logout here, then clear:
    setMe(null);
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const value = useMemo<SessionCtx>(
    () => ({
      me,
      isAuthenticated: !!me?.userId,
      loading,
      error,
      refresh,
      logout,
    }),
    [me, loading, error, refresh, logout]
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession() {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error('useSession must be used within <SessionProvider>');
  return ctx;
}
